'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Star, Shield, ChevronRight, ArrowRight, Pill, AlertCircle, Truck, Phone } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface PharmacyProduct {
  id: string;
  name: string;
  pharmacy: string;
  pharmacy_city: string;
  price: number;
  category: string;
  requires_prescription: boolean;
  in_stock: boolean;
  rating: number;
  reviews: number;
  image_url: string;
  description: string;
  delivery_available: boolean;
}

interface PharmacyStore {
  id: string;
  name: string;
  location: string;
  city: string;
  rating: number;
  reviews: number;
  is_verified: boolean;
  is_open: boolean;
  phone: string;
  delivery: boolean;
  image_url: string;
}

const PRODUCTS: PharmacyProduct[] = [
{ id: '1', name: 'Paracetamol 500mg (24 tabs)', pharmacy: 'Nairobi Pharmacy Plus', pharmacy_city: 'Nairobi', price: 85, category: 'Pain Relief', requires_prescription: false, in_stock: true, rating: 4.8, reviews: 234, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa3b73f8-1783588260335.png", description: 'Fast-acting pain and fever relief. Suitable for adults and children over 12.', delivery_available: true },
{ id: '2', name: 'Vitamin C 1000mg (30 tabs)', pharmacy: 'MedCare Chemist', pharmacy_city: 'Nairobi', price: 450, category: 'Supplements', requires_prescription: false, in_stock: true, rating: 4.7, reviews: 189, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_184533285-1784530799177.png", description: 'High-strength immune support. Effervescent formula for fast absorption.', delivery_available: true },
{ id: '3', name: 'Ibuprofen 400mg (24 tabs)', pharmacy: 'Kisumu Health Pharmacy', pharmacy_city: 'Kisumu', price: 120, category: 'Pain Relief', requires_prescription: false, in_stock: true, rating: 4.6, reviews: 156, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa3b73f8-1783588260335.png", description: 'Anti-inflammatory pain relief for headaches, muscle pain, and fever.', delivery_available: false },
{ id: '4', name: 'Oral Rehydration Salts (10 sachets)', pharmacy: 'Nairobi Pharmacy Plus', pharmacy_city: 'Nairobi', price: 95, category: 'First Aid', requires_prescription: false, in_stock: true, rating: 4.9, reviews: 312, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecdf1663-1778786342644.png", description: 'WHO-formula rehydration therapy for diarrhoea and dehydration.', delivery_available: true },
{ id: '5', name: 'Malaria Rapid Test Kit', pharmacy: 'MedCare Chemist', pharmacy_city: 'Nairobi', price: 350, category: 'Diagnostics', requires_prescription: false, in_stock: true, rating: 4.5, reviews: 98, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_18b9bd830-1776156801943.png", description: 'Fast and accurate malaria detection in 15 minutes. CE certified.', delivery_available: true },
{ id: '6', name: 'Zinc Supplement 20mg (30 tabs)', pharmacy: 'Kisumu Health Pharmacy', pharmacy_city: 'Kisumu', price: 280, category: 'Supplements', requires_prescription: false, in_stock: false, rating: 4.4, reviews: 67, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_16a87f312-1772825553090.png", description: 'Essential mineral for immune function and wound healing.', delivery_available: false }];


const PHARMACIES: PharmacyStore[] = [
{ id: '1', name: 'Nairobi Pharmacy Plus', location: 'Tom Mboya Street', city: 'Nairobi', rating: 4.8, reviews: 567, is_verified: true, is_open: true, phone: '+254711001001', delivery: true, image_url: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&q=80' },
{ id: '2', name: 'MedCare Chemist', location: 'Kenyatta Avenue', city: 'Nairobi', rating: 4.7, reviews: 423, is_verified: true, is_open: true, phone: '+254711001002', delivery: true, image_url: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=400&q=80' },
{ id: '3', name: 'Kisumu Health Pharmacy', location: 'Oginga Odinga Street', city: 'Kisumu', rating: 4.5, reviews: 234, is_verified: false, is_open: false, phone: '+254711001003', delivery: false, image_url: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400&q=80' }];


const CATEGORIES = ['All', 'Pain Relief', 'Supplements', 'First Aid', 'Diagnostics', 'Prescription'];
const CITIES = ['All Cities', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];

export default function PharmacyLandingPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [city, setCity] = useState('All Cities');
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [view, setView] = useState<'products' | 'pharmacies'>('products');

  const filteredProducts = PRODUCTS.filter((p) => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.pharmacy.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || p.category === category || category === 'Prescription' && p.requires_prescription;
    const matchCity = city === 'All Cities' || p.pharmacy_city === city;
    const matchDelivery = !deliveryOnly || p.delivery_available;
    const matchStock = !inStockOnly || p.in_stock;
    return matchSearch && matchCat && matchCity && matchDelivery && matchStock;
  });

  const filteredPharmacies = PHARMACIES.filter((p) => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchCity = city === 'All Cities' || p.city === city;
    const matchDelivery = !deliveryOnly || p.delivery;
    return matchSearch && matchCity && matchDelivery;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001a2e 0%, #001a1a 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #40C4FF 0%, transparent 50%), radial-gradient(circle at 70% 30%, #00BCD4 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
            style={{ background: 'rgba(64,196,255,0.15)', color: '#40C4FF', border: '1px solid rgba(64,196,255,0.3)' }}>
                <Pill size={12} /> {3 .toLocaleString()} Verified Pharmacies
              </div>
              <Link href="/create-listing?type=pharmacy" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#40C4FF', color: '#000' }}>
                + Add Pharmacy
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Order Medicine <span style={{ color: '#40C4FF' }}>Online</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Browse medicines, supplements, and health products from verified pharmacies. Fast delivery available.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search medicines or pharmacies…"
              className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
            {/* Safety notice */}
            <div className="flex items-center gap-2 justify-center mt-4 px-4 py-2 rounded-xl mx-auto max-w-sm"
            style={{ background: 'rgba(255,179,0,0.08)', border: '1px solid rgba(255,179,0,0.2)' }}>
              <AlertCircle size={12} style={{ color: '#FFB300', flexShrink: 0 }} />
              <p className="text-xs" style={{ color: '#FFB300' }}>Prescription medicines require a valid prescription from a licensed doctor.</p>
            </div>
          </div>
        </div>

        {/* View toggle + filters */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => setView('products')}
              className="px-4 py-2 rounded-xl text-xs font-700 transition-all"
              style={{ background: view === 'products' ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.04)', color: view === 'products' ? '#40C4FF' : 'rgba(255,255,255,0.55)', border: `1px solid ${view === 'products' ? 'rgba(64,196,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                Products
              </button>
              <button onClick={() => setView('pharmacies')}
              className="px-4 py-2 rounded-xl text-xs font-700 transition-all"
              style={{ background: view === 'pharmacies' ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.04)', color: view === 'pharmacies' ? '#40C4FF' : 'rgba(255,255,255,0.55)', border: `1px solid ${view === 'pharmacies' ? 'rgba(64,196,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                Pharmacies
              </button>
              <div className="flex-1" />
              <button onClick={() => setDeliveryOnly(!deliveryOnly)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 transition-all"
              style={{ background: deliveryOnly ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: deliveryOnly ? '#00C853' : 'rgba(255,255,255,0.5)', border: `1px solid ${deliveryOnly ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                <Truck size={11} /> Delivery
              </button>
              <button onClick={() => setInStockOnly(!inStockOnly)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 transition-all"
              style={{ background: inStockOnly ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.04)', color: inStockOnly ? '#40C4FF' : 'rgba(255,255,255,0.5)', border: `1px solid ${inStockOnly ? 'rgba(64,196,255,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                In Stock
              </button>
            </div>
            {view === 'products' &&
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {CATEGORIES.map((c) =>
              <button key={c} onClick={() => setCategory(c)}
              className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
              style={{ background: category === c ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#40C4FF' : 'rgba(255,255,255,0.5)', border: `1px solid ${category === c ? 'rgba(64,196,255,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                    {c}
                  </button>
              )}
                <div className="flex-shrink-0 w-px h-4 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
                {CITIES.map((c) =>
              <button key={c} onClick={() => setCity(c)}
              className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
              style={{ background: city === c ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.04)', color: city === c ? '#40C4FF' : 'rgba(255,255,255,0.5)', border: `1px solid ${city === c ? 'rgba(64,196,255,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                    {c}
                  </button>
              )}
              </div>
            }
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          {view === 'products' ?
          <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  <span style={{ color: '#40C4FF', fontWeight: 700 }}>{filteredProducts.length}</span> products found
                </span>
                <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#40C4FF', color: '#000' }}>
                  Order Now <ArrowRight size={12} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredProducts.map((product) =>
              <div key={product.id} className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="relative h-36">
                      <img src={product.image_url} alt={product.name}
                  className="w-full h-full object-cover" loading="lazy" />
                      {product.requires_prescription &&
                  <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                  style={{ background: 'rgba(255,59,92,0.9)', color: '#fff' }}>Rx</span>
                  }
                      {!product.in_stock &&
                  <div className="absolute inset-0 flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.6)' }}>
                          <span className="text-xs font-700 text-white">Out of Stock</span>
                        </div>
                  }
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-700 text-white line-clamp-2 leading-tight">{product.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{product.pharmacy}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-sm font-800" style={{ color: '#40C4FF' }}>KES {product.price}</p>
                        <div className="flex items-center gap-0.5">
                          <Star size={9} fill="#FFB300" style={{ color: '#FFB300' }} />
                          <span className="text-[10px]" style={{ color: '#FFB300' }}>{product.rating}</span>
                        </div>
                      </div>
                      {product.delivery_available &&
                  <div className="flex items-center gap-1 mt-1">
                          <Truck size={9} style={{ color: '#00C853' }} />
                          <span className="text-[10px]" style={{ color: '#00C853' }}>Delivery available</span>
                        </div>
                  }
                      <Link href="/login"
                  className="block text-center mt-2 py-1.5 rounded-xl text-xs font-700"
                  style={{ background: product.in_stock ? 'rgba(64,196,255,0.15)' : 'rgba(255,255,255,0.05)', color: product.in_stock ? '#40C4FF' : 'rgba(255,255,255,0.3)' }}>
                        {product.in_stock ? 'Add to Cart' : 'Notify Me'}
                      </Link>
                    </div>
                  </div>
              )}
              </div>
            </> :

          <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  <span style={{ color: '#40C4FF', fontWeight: 700 }}>{filteredPharmacies.length}</span> pharmacies found
                </span>
                <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#40C4FF', color: '#000' }}>
                  Register Pharmacy <ArrowRight size={12} />
                </Link>
              </div>
              <div className="flex flex-col gap-4">
                {filteredPharmacies.map((pharmacy) =>
              <div key={pharmacy.id} className="flex gap-3 p-4 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <img src={pharmacy.image_url} alt={`${pharmacy.name} storefront`}
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-700 text-white">{pharmacy.name}</h3>
                        {pharmacy.is_verified && <Shield size={12} style={{ color: '#00C853' }} />}
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md font-600"
                    style={{ background: pharmacy.is_open ? 'rgba(0,200,83,0.15)' : 'rgba(255,59,92,0.15)', color: pharmacy.is_open ? '#00C853' : '#FF3B5C' }}>
                          {pharmacy.is_open ? 'Open' : 'Closed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{pharmacy.location}, {pharmacy.city}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex items-center gap-1">
                          <Star size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                          <span className="text-xs font-700" style={{ color: '#FFB300' }}>{pharmacy.rating}</span>
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>({pharmacy.reviews})</span>
                        </div>
                        {pharmacy.delivery &&
                    <div className="flex items-center gap-1">
                            <Truck size={10} style={{ color: '#00C853' }} />
                            <span className="text-xs" style={{ color: '#00C853' }}>Delivery</span>
                          </div>
                    }
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <a href={`tel:${pharmacy.phone}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-600"
                    style={{ background: 'rgba(64,196,255,0.1)', color: '#40C4FF', border: '1px solid rgba(64,196,255,0.2)' }}>
                          <Phone size={10} /> Call
                        </a>
                        <Link href="/login"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-600"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>
                          View Products <ChevronRight size={10} />
                        </Link>
                      </div>
                    </div>
                  </div>
              )}
              </div>
            </>
          }

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(64,196,255,0.1), rgba(0,188,212,0.1))', border: '1px solid rgba(64,196,255,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">Order Medicine Online</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to order medicines, upload prescriptions, and track deliveries.</p>
            <Link href="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
            style={{ background: '#40C4FF', color: '#000' }}>
              <Pill size={16} /> Order Now
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>);

}